import { useState, useEffect } from 'react';
import { kommunApi } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Building2, Cpu, AlertTriangle, TrendingUp, FileText,
  Download, Loader2, Shield, Wifi, WifiOff, BarChart3
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend
} from 'recharts';

export default function KommunDashboardPage() {
  const [dashboard, setDashboard] = useState(null);
  const [schools, setSchools] = useState([]);
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [dashRes, schoolsRes, trendsRes] = await Promise.all([
        kommunApi.dashboard(),
        kommunApi.schools(),
        kommunApi.trends(),
      ]);
      setDashboard(dashRes.data);
      setSchools(schoolsRes.data);
      setTrends(trendsRes.data);
    } catch {
      toast.error('Kunde inte ladda kommundata');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = async () => {
    setDownloading(true);
    try {
      const res = await kommunApi.reportPdf();
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'vapelarm_kommunrapport.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Kommunrapport nedladdad');
    } catch {
      toast.error('Kunde inte generera rapport');
    } finally {
      setDownloading(false);
    }
  };

  // Build chart data: aggregate trends by week
  const chartData = (() => {
    const byWeek = {};
    trends.forEach(t => {
      const key = `V${t.week}`;
      if (!byWeek[key]) byWeek[key] = { week: key };
      byWeek[key][t.school_name] = (byWeek[key][t.school_name] || 0) + t.total;
    });
    return Object.values(byWeek);
  })();
  const schoolNames = [...new Set(trends.map(t => t.school_name))];
  const COLORS = ['#2563eb', '#059669', '#d97706', '#dc2626', '#7c3aed', '#0d9488'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const d = dashboard || {};

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="kommun-dashboard">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-chivo font-black text-2xl md:text-3xl text-navy dark:text-white tracking-tight">
            Kommunöversikt
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1 font-ibm">
            Aggregerad statistik för alla skolor i kommunen
          </p>
        </div>
        <Button
          onClick={handleDownloadReport}
          disabled={downloading}
          className="bg-navy hover:bg-navy/90 font-ibm"
          data-testid="download-kommun-report-btn"
        >
          {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
          Ladda ner månadsrapport
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card data-testid="stat-schools">
          <CardContent className="p-4 text-center">
            <Building2 className="h-6 w-6 mx-auto mb-2 text-blue-600" />
            <p className="text-2xl font-chivo font-black text-navy dark:text-white">{d.schools_count || 0}</p>
            <p className="text-xs text-slate-500 font-ibm">Skolor</p>
          </CardContent>
        </Card>
        <Card data-testid="stat-devices">
          <CardContent className="p-4 text-center">
            <Cpu className="h-6 w-6 mx-auto mb-2 text-emerald-600" />
            <p className="text-2xl font-chivo font-black text-navy dark:text-white">{d.total_devices || 0}</p>
            <p className="text-xs text-slate-500 font-ibm">Sensorer</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 dark:border-red-800" data-testid="stat-vape">
          <CardContent className="p-4 text-center">
            <AlertTriangle className="h-6 w-6 mx-auto mb-2 text-red-600" />
            <p className="text-2xl font-chivo font-black text-red-600">{d.vape_events_30d || 0}</p>
            <p className="text-xs text-slate-500 font-ibm">Vape-larm (30d)</p>
          </CardContent>
        </Card>
        <Card className="border-amber-200 dark:border-amber-800" data-testid="stat-suspect">
          <CardContent className="p-4 text-center">
            <TrendingUp className="h-6 w-6 mx-auto mb-2 text-amber-600" />
            <p className="text-2xl font-chivo font-black text-amber-600">{d.suspect_events_30d || 0}</p>
            <p className="text-xs text-slate-500 font-ibm">Misstänkta (30d)</p>
          </CardContent>
        </Card>
        <Card data-testid="stat-incidents">
          <CardContent className="p-4 text-center">
            <FileText className="h-6 w-6 mx-auto mb-2 text-violet-600" />
            <p className="text-2xl font-chivo font-black text-navy dark:text-white">{d.open_incidents || 0}</p>
            <p className="text-xs text-slate-500 font-ibm">Öppna ärenden</p>
          </CardContent>
        </Card>
        <Card data-testid="stat-online">
          <CardContent className="p-4 text-center">
            <Wifi className="h-6 w-6 mx-auto mb-2 text-green-600" />
            <p className="text-2xl font-chivo font-black text-green-600">{d.online_devices || 0}<span className="text-sm text-slate-400">/{d.total_devices || 0}</span></p>
            <p className="text-xs text-slate-500 font-ibm">Online</p>
          </CardContent>
        </Card>
      </div>

      {/* Trend Chart */}
      {chartData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="font-chivo text-lg flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Händelser per vecka och skola
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '12px' }} />
                  <Legend />
                  {schoolNames.map((name, i) => (
                    <Bar key={name} dataKey={name} fill={COLORS[i % COLORS.length]} stackId="a" />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* School Breakdown Table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-chivo text-lg flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Statistik per skola (senaste 30 dagarna)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="kommun-schools-table">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700">
                  <th className="text-left py-3 px-2 font-ibm font-semibold text-slate-600 dark:text-slate-300">Skola</th>
                  <th className="text-center py-3 px-2 font-ibm font-semibold text-slate-600 dark:text-slate-300">Sensorer</th>
                  <th className="text-center py-3 px-2 font-ibm font-semibold text-slate-600 dark:text-slate-300">Online</th>
                  <th className="text-center py-3 px-2 font-ibm font-semibold text-slate-600 dark:text-slate-300">Vape</th>
                  <th className="text-center py-3 px-2 font-ibm font-semibold text-slate-600 dark:text-slate-300">Misst.</th>
                  <th className="text-center py-3 px-2 font-ibm font-semibold text-slate-600 dark:text-slate-300">Totalt</th>
                  <th className="text-center py-3 px-2 font-ibm font-semibold text-slate-600 dark:text-slate-300">Öppna ärenden</th>
                  <th className="text-center py-3 px-2 font-ibm font-semibold text-slate-600 dark:text-slate-300">Stängda</th>
                </tr>
              </thead>
              <tbody>
                {schools.map((s, i) => (
                  <tr key={s.id} className={i % 2 === 0 ? 'bg-slate-50 dark:bg-slate-800/50' : ''}>
                    <td className="py-3 px-2">
                      <div>
                        <span className="font-chivo font-bold text-navy dark:text-white">{s.name}</span>
                        {s.address && <p className="text-xs text-slate-400">{s.address}</p>}
                      </div>
                    </td>
                    <td className="text-center py-3 px-2 font-ibm">{s.devices_count}</td>
                    <td className="text-center py-3 px-2">
                      <Badge variant={s.offline_devices === 0 ? 'default' : 'destructive'} className={s.offline_devices === 0 ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : ''}>
                        {s.online_devices}/{s.devices_count}
                      </Badge>
                    </td>
                    <td className="text-center py-3 px-2">
                      <span className={`font-chivo font-bold ${s.vape_events_30d > 0 ? 'text-red-600' : 'text-slate-400'}`}>
                        {s.vape_events_30d}
                      </span>
                    </td>
                    <td className="text-center py-3 px-2 font-ibm text-amber-600">{s.suspect_events_30d}</td>
                    <td className="text-center py-3 px-2 font-chivo font-bold text-navy dark:text-white">{s.total_events_30d}</td>
                    <td className="text-center py-3 px-2">
                      {s.open_incidents > 0 ? (
                        <Badge variant="destructive">{s.open_incidents}</Badge>
                      ) : (
                        <span className="text-green-600 font-ibm">0</span>
                      )}
                    </td>
                    <td className="text-center py-3 px-2 font-ibm text-slate-500">{s.closed_incidents_30d}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
